/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  GoogleMap,
  Marker,
  InfoWindow,
} from '@react-google-maps/api'
import Carousel from 'react-material-ui-carousel'
import groupBy from 'lodash/groupBy'
import GroupEventCard from '@components/group-event-card'
import Box from '@material-ui/core/Box'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Drawer from 'react-bottom-drawer'
import { useLoadScripts } from '@lib/google/places/browser'
import { getAllIndex } from '@components/group-events-search/GroupEventsResults/utils'

const mapContainerStyle = {
  height: 'calc(100vh - 155px)',
  width: '100%',
}

const options = {
  backgroundColor: '#f5f5f5',
}


const MapViewResults = (props) => {
  const {
    hits,
    selectedHit,
    cardHoveredHit,
    setSelected,
    searchState,
    aroundRadius,
    aroundIpLatLng,
  } = props
  const resetAll = searchState?.reset === 'all'

  // todo define and setup default coordinates for Virtual Events
  const {
    aroundLatLng,
    refinementList,
    configure,
    multiRange,
    range,
    toggle,
  } = searchState

  const [aroundLat, aroundLng] = aroundLatLng?.split(',') || aroundIpLatLng?.split(',') || [0, 0]
  const [allHits, setAllHits] = useState([])

  useEffect(() => {
    const response = getAllIndex({
      aroundLatLng: resetAll ? undefined : aroundLatLng,
      aroundRadius: resetAll ? undefined : aroundRadius,
      tags: refinementList?.tags || [],
      priceInCents: multiRange?.price_in_cents,
      totalPriceInCents: multiRange?.total_price_in_cents,
      startDateTime: range?.start_date_time_ts,
      registrationFull: toggle?.registration_full,
    })
    response.then((result) => {
      setAllHits(result)
    }).catch(err => console.log(err))
  }, [configure, refinementList, aroundRadius])

  const defaultCenter = {
    lat: hits.filter(hit => hit?._geoloc)[0]?._geoloc.lat || +aroundLat || 0,
    lng: hits.filter(hit => hit?._geoloc)[0]?._geoloc.lng || +aroundLng || 0,
  }

  const isMobile = useMediaQuery('(max-width:959.9px)', { noSsr: true })
  const xsMobile = useMediaQuery('(max-width:600px)')

  const [isDrawerVisible, setIsDrawerVisible] = React.useState(false)

  const onClose = React.useCallback(() => {
    setIsDrawerVisible(false)
    setSelected([])
  }, [])

  const { isLoaded, loadError } = useLoadScripts()

  const [center, setCenter] = React.useState(defaultCenter)

  useEffect(() => {
    setCenter(defaultCenter)
  }, [hits])

  if (loadError) return 'Error'
  if (!isLoaded) return 'Loading...'

  const groupedHits = groupBy(allHits.filter(hit => hit._geoloc), (group) => {
    return [group._geoloc.lat, group._geoloc.lng]
  })

  const marker = markerColor => ({
    path: 'M17 5.92 9 2v18H7v-1.73c-1.79.35-3 .99-3 1.73 0 1.1 2.69 2 6 2s6-.9 6-2c0-.99-2.16-1.81-5-1.97V8.98l6-3.06z',
    fillColor: markerColor,
    fillOpacity: 1,
    strokeWeight: 0,
    rotation: 0,
    scale: 2,
  })

  const hoveredMarker = markerColor => ({
    path: 'M17 5.92 9 2v18H7v-1.73c-1.79.35-3 .99-3 1.73 0 1.1 2.69 2 6 2s6-.9 6-2c0-.99-2.16-1.81-5-1.97V8.98l6-3.06z',
    fillColor: 'white',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: markerColor,
    rotation: 0,
    scale: 2,
  })

  return (
    <div>
      <GoogleMap
        id="map"
        mapContainerStyle={mapContainerStyle}
        zoom={9}
        center={center}
        options={options}
        onClick={() => {
          setSelected([])
        }}
      >
        {Object.entries(groupedHits).flatMap((group) => {
          let currentHit = group[1].filter(item => item.objectID === cardHoveredHit?.objectID)
          currentHit = currentHit.length > 0 ? currentHit : group[1]
          const markerColor = currentHit[0]?.member_type ? '#043362' : '#00234B99'
          const lat = parseFloat(group[0].split(',')[0])
          const lng = parseFloat(group[0].split(',')[1])
          return (
            <Marker
              key={`${lat}-${lng}-${Math.random()}`}
              position={{ lat, lng }}
              onClick={() => {
                setSelected(group[1])
                setIsDrawerVisible(true)
              }}
              icon={
                currentHit[0]?.objectID.includes(cardHoveredHit?.objectID)
                  ? hoveredMarker(markerColor)
                  : marker(markerColor)
              }
            />
          )
        })}
        {(selectedHit.length > 0 && !isMobile) && (
          <InfoWindow
            position={{ lat: selectedHit[0]._geoloc.lat, lng: selectedHit[0]._geoloc.lng }}
            onCloseClick={() => {
              setSelected([])
            }}
          >
            <Box maxWidth={250}>
              {selectedHit.length > 1 ? (
                <Carousel
                  fullHeightHover={false}
                  navButtonsProps={{
                    style: {
                      backgroundColor: '#253255',
                      margin: '0px 0px',
                      height: '25px',
                      width: '25px',
                    },
                  }}
                  navButtonsWrapperProps={{
                    style: {
                      top: 'calc(100% - 65px)',
                    },
                  }}
                  indicators={false}
                  autoPlay={false}
                  navButtonsAlwaysVisible
                  indicatorContainerProps={{
                    style: {
                      bottom: '50px',
                    },
                  }}
                  // https://github.com/Learus/react-material-ui-carousel/issues/20
                  timeout={{
                    appear: 1500,
                    enter: 1500,
                    exit: 0,
                  }}
                >
                  {selectedHit.map((hit, index) => {
                    return (
                      <Box
                        pb={0}
                        key={hit.objectID}
                      >
                        <h3 style={{ textAlign: 'center' }}>
                          <span style={{ fontWeight: 'normal' }}>
                            {hit.coach_facility_name}
                          </span>
                        </h3>
                        <GroupEventCard
                          {...hit}
                          withPrice
                          cardStyle="column"
                          isMapInfo
                          source="event-search-directory"
                        />
                        <h3 style={{ textAlign: 'center', borderTop: '1px solid #E0E0E0', paddingTop: '10px' }}>
                          <span style={{ fontWeight: 'normal' }}>
                            {`Event ${index + 1} of ${selectedHit.length}`}
                          </span>
                        </h3>
                      </Box>
                    )
                  })}
                </Carousel>
              ) : (
                <GroupEventCard
                  {...selectedHit[0]}
                  withPrice
                  cardStyle="column"
                  source="event-search-directory"
                />
              )}
            </Box>
          </InfoWindow>
        )}
      </GoogleMap>
      {(selectedHit.length > 0 && isMobile) && (
        <Drawer
          isVisible={isDrawerVisible}
          onClose={onClose}
        >
          <h3 style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: 'normal' }}>
              {selectedHit[0].coach_facility_name}
            </span>
          </h3>
          {selectedHit.map((hit) => {
            return (
              <div
                key={hit.objectID}
                style={{ borderBottom: '1px solid #E0E0E0', padding: '10px 0px' }}
              >
                <GroupEventCard
                  {...hit}
                  withPrice
                  cardStyle="row"
                  isMapInfo
                  squareImage={xsMobile}
                  source="event-search-directory"
                />
              </div>
            )
          })}
        </Drawer>
      )}
    </div>
  )
}

MapViewResults.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object),
  setSelected: PropTypes.func,
  cardHoveredHit: PropTypes.object,
  selectedHit: PropTypes.array,
  searchState: PropTypes.shape({
    aroundLatLng: PropTypes.string,
    placeName: PropTypes.string,
    refinementList: PropTypes.object,
    configure: PropTypes.object,
    multiRange: PropTypes.object,
    range: PropTypes.object,
    toggle: PropTypes.object,
    reset: PropTypes.string,
  }),
  aroundRadius: PropTypes.number,
  aroundIpLatLng: PropTypes.string,
}

MapViewResults.defaultProps = {
  hits: [],
  cardHoveredHit: {},
  selectedHit: [],
}

export default MapViewResults
